/**
 * DebugLogViewer — the user-facing surface for the mini-app debug log.
 *
 * Opened from the "Debug log" file context-menu item for a `.miniapp` folder or
 * a loose HTML file. Fetches the log via the permission-gated debug-log route
 * (owner/editor only — a 403 renders a "no access" state) and renders the NDJSON
 * across two tabs:
 *
 *   - Logs     — the captured output: newest session first, errors highlighted,
 *                source + level badges, per-entry stack/URL detail, filtered by
 *                level / file / free text, with a scoped Clear action.
 *   - Sessions — the run history. `session_start` markers are META, not app
 *                output: one lands in the log every time anyone opens the app,
 *                so leaving them inline buried the actual console output. They
 *                render here instead, one readable card per run (when, how long,
 *                which browser, which files, how many errors).
 *
 * Rendered outside the ThemedArea hierarchy (it's a portal-style overlay), so it
 * applies the workspace theme class manually — see CLAUDE.md "Components Outside
 * ThemedArea".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { debugLogApi, debugLogTargetFor, appRunLogApi, type DebugLogEntry } from '@papan/files/client'
import { Bug, X, RefreshCw, Trash2, AlertCircle, FileCode, Server, ChevronDown, Monitor, User } from 'lucide-react'
import { AppRunHistory } from './AppRunHistory.js'
import { describeUa } from './ua.js'

/** Close an in-modal popup on outside pointerdown / Escape. */
function usePopupDismiss(ref: React.RefObject<HTMLDivElement | null>, open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return
    const onPointerDown = (ev: PointerEvent) => {
      if (!ref.current?.contains(ev.target as Node)) close()
    }
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') close() }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [ref, open, close])
}

interface DebugLogViewerProps {
  file: { id: string; name: string; isFolder: boolean }
  onClose: () => void
  /** Open on the Runs lane (the platform's record of failed runs) — from an app's "database writes failed" notice. */
  initialFilter?: 'runs'
}

const LEVEL_STYLE: Record<string, string> = {
  error: 'text-red-500',
  warn: 'text-amber-500',
  network: 'text-orange-500',
  info: 'text-blue-400',
  debug: 'themed-text-muted',
  log: 'themed-text-secondary',
}

const SRC_LABEL: Record<string, string> = { browser: 'browser', backend: 'backend', meta: 'session' }

// An entry is "long" (collapsed to a 2-line clamp until clicked) when it has a
// stack, spans MORE lines than the clamp shows, or overflows what two lines
// can plausibly hold. A short 2-line message fits the clamp whole — treating
// it as long would be a dead affordance (pointer + "expand" that changes
// nothing on click).
const LONG_MSG_CHARS = 240
function isLongEntry(e: DebugLogEntry): boolean {
  if (e.stack) return true
  const msg = e.msg ?? ''
  return msg.length > LONG_MSG_CHARS || msg.split('\n').length > 2
}

function fmtTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    return String(ts)
  }
}

/** Session-header date: weekday + full date + time + timezone, so a log that
 *  spans days is unambiguous while entry rows stay time-only. */
function fmtSessionDate(ts: number): string {
  try {
    return new Date(ts).toLocaleString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short',
    })
  } catch {
    return String(ts)
  }
}

/** Compact, locale-neutral run length (`4m 12s`) — the unit letters read the
 *  same way the tabular counts elsewhere in this modal do. */
function fmtDuration(ms: number): string {
  if (ms < 1000) return '<1s'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

/** How many files a session card names before collapsing into "+N". */
const SESSION_FILES_SHOWN = 4

export function DebugLogViewer({ file, onClose, initialFilter }: DebugLogViewerProps) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const target = useMemo(() => debugLogTargetFor(file), [file])

  const [entries, setEntries] = useState<DebugLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<'forbidden' | 'failed' | null>(null)
  const [tab, setTab] = useState<'logs' | 'sessions'>('logs')
  // Bumped after a run-history clear, so the lazily-fetched panel re-reads
  // instead of showing rows the server has just deleted.
  const [runsRefresh, setRunsRefresh] = useState(0)
  // Reported UP by AppRunHistory once its own (separate, lazy) fetch lands.
  // `null` until then, and back to `null` on failure — the header must not
  // print a count for a request that never answered.
  const [runCount, setRunCount] = useState<number | null>(null)
  /**
   * The Logs tab's lane selector. `'runs'` is NOT a console level — it swaps the
   * body for the platform's own record of what happened AROUND a script run.
   *
   * One piece of state rather than a level plus a boolean, because the control is
   * one row where exactly one chip is active: a separate flag would allow
   * `runs + error`, a state the UI has no rendering for.
   */
  const [levelFilter, setLevelFilter] = useState<'all' | 'error' | 'warn' | 'network' | 'runs'>(initialFilter ?? 'all')
  /** True when the Logs tab is showing the run lane rather than console output. */
  const onRuns = tab === 'logs' && levelFilter === 'runs'
  const [fileFilter, setFileFilter] = useState<string>('all')
  // Set by clicking a card on the Sessions tab — the one place a session id is
  // a useful handle. Never typed by hand; cleared from the chip in the filter row.
  const [sessionFilter, setSessionFilter] = useState<string | null>(null)
  const [text, setText] = useState('')

  /**
   * Jump from a Sessions card to that run's logs.
   *
   * Clears the other filters deliberately. A card states its counts UNFILTERED
   * ("6 entries · 1 error"), so landing on a view still narrowed by a filter set
   * on the other tab contradicts the thing just clicked — at worst silently, on
   * an empty body ("No entries match the current filter") for a card that
   * promised entries. The card is a "show me this run" navigation, so it lands
   * on exactly the run it advertised; re-narrowing from there is one click.
   */
  const openSession = useCallback((sess: string) => {
    setSessionFilter(sess)
    setFileFilter('all')
    setLevelFilter('all')
    setText('')
    setTab('logs')
  }, [])
  // Long entries the user clicked open. Keyed by OBJECT IDENTITY — the entry
  // refs are stable across filtering (the memo filters, never clones), and a
  // reload replaces them wholesale, so the set is reset there.
  const [expanded, setExpanded] = useState<Set<DebugLogEntry>>(() => new Set())
  const toggleExpanded = useCallback((e: DebugLogEntry) => {
    // Don't hijack a text-selection drag as an expand/collapse click.
    if (window.getSelection()?.toString()) return
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(e)) next.delete(e)
      else next.add(e)
      return next
    })
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setEntries(await debugLogApi.get(target.kind, target.id))
      setExpanded(new Set())
    } catch (e) {
      const status = (e as { status?: number })?.status
      setError(status === 403 ? 'forbidden' : 'failed')
    } finally {
      setLoading(false)
    }
  }, [target.kind, target.id])

  useEffect(() => { void load() }, [load])

  // Clear popup — the trash icon opens a scoped-clear menu (last hour / 3
  // hours / day / all) instead of nuking the log outright.
  const [showClearMenu, setShowClearMenu] = useState(false)
  const clearMenuRef = useRef<HTMLDivElement>(null)
  const closeClearMenu = useCallback(() => setShowClearMenu(false), [])
  usePopupDismiss(clearMenuRef, showClearMenu, closeClearMenu)

  // Small-screen level filter — the chip row collapses into a glass dropdown
  // below the `sm` breakpoint (the four chips + text input don't fit).
  const [showLevelMenu, setShowLevelMenu] = useState(false)
  const levelMenuRef = useRef<HTMLDivElement>(null)
  const closeLevelMenu = useCallback(() => setShowLevelMenu(false), [])
  usePopupDismiss(levelMenuRef, showLevelMenu, closeLevelMenu)

  // File filter — a miniapp's log is CONSOLIDATED across every page and backend
  // script in the app, so "which file" is the axis that actually narrows it.
  const [showFileMenu, setShowFileMenu] = useState(false)
  const fileMenuRef = useRef<HTMLDivElement>(null)
  const closeFileMenu = useCallback(() => setShowFileMenu(false), [])
  usePopupDismiss(fileMenuRef, showFileMenu, closeFileMenu)

  /**
   * Clear the RUN history — a different record from the console log beside it,
   * so a different action.
   *
   * `runsRefresh` is bumped rather than the rows being cleared locally: the
   * server decides what a scoped clear leaves behind, and guessing here would
   * put a number on screen that the next fetch contradicts.
   *
   * The count is deliberately NOT reset to 0 optimistically — `onCount(null)`
   * semantics say "unknown until a fetch resolves", and claiming zero failures
   * for a request that has not answered is the one thing this panel exists to
   * never do.
   */
  const clearRuns = useCallback(async (withinMs?: number) => {
    setShowClearMenu(false)
    try {
      await appRunLogApi.clear(file.id, withinMs)
      setRunCount(null)
      setRunsRefresh((n) => n + 1)
    } catch { /* best-effort; the next refresh shows the truth either way */ }
  }, [file.id])

  const clear = useCallback(async (withinMs?: number) => {
    setShowClearMenu(false)
    try {
      await debugLogApi.clear(target.kind, target.id, withinMs)
      if (withinMs === undefined) {
        setEntries([])
        setExpanded(new Set())
      } else {
        // Scoped clear: the server decides which entries survive — re-read.
        await load()
      }
    } catch { /* best-effort; leave current view */ }
  }, [target.kind, target.id, load])

  // Which in-app file (or the backend) produced an entry — the consolidated
  // miniapp log tags entries with `file` (browser pages AND backend script
  // tees); older backend entries only carry `script`. Empty for Mode A /
  // per-file views (single attribution → no section headers rendered).
  const attributionOf = (e: DebugLogEntry): string =>
    e.file ?? (e.src === 'backend' ? `backend${e.script ? ` · ${e.script}` : ''}` : '')

  // `session_start` markers are run METADATA, not app output — one is appended
  // every time anyone opens the app, so inline they were pure noise between the
  // console lines this tab exists to show. They're still STORED (retention keys
  // sessions off them, and the Sessions tab reads them) — only split out here.
  const logEntries = useMemo(() => entries.filter((e) => e.event !== 'session_start'), [entries])

  // Run history for the Sessions tab, newest first. Derived from ALL entries:
  // the `session_start` marker carries the ua/actor, the rest of the session's
  // entries carry its span, files and error count. A session with no marker
  // still lists (the `sess:'backend'` pseudo-session for script tees, or a
  // marker aged out from under its own entries).
  const sessions = useMemo(() => {
    const byId = new Map<string, {
      sess: string; start: number; end: number; ua?: string; actor?: string
      total: number; errors: number; files: Set<string>
    }>()
    for (const e of entries) {
      let s = byId.get(e.sess)
      if (!s) {
        s = { sess: e.sess, start: e.ts, end: e.ts, total: 0, errors: 0, files: new Set() }
        byId.set(e.sess, s)
      }
      s.start = Math.min(s.start, e.ts)
      s.end = Math.max(s.end, e.ts)
      if (e.event === 'session_start') {
        s.ua = e.ua ?? s.ua
        s.actor = e.actor ?? s.actor
      } else {
        // Counts describe the app OUTPUT — the marker isn't an entry the Logs
        // tab will show, so counting it would misreport every card by one.
        s.total++
        if (e.lvl === 'error') s.errors++
      }
      // …but the marker's own `file` is the page the run OPENED, which is worth
      // naming even for a run that logged nothing else (the common case: a log
      // that is nothing but markers). So attribute from every entry, marker
      // included — it's "files this session touched", not "files that logged".
      const attr = attributionOf(e)
      if (attr) s.files.add(attr)
    }
    return [...byId.values()].sort((a, b) => b.end - a.end)
  }, [entries])

  // The distinct files the log spans — the file-filter options. Computed from
  // the UNFILTERED entries so the menu doesn't shrink to whatever's on screen.
  const fileOptions = useMemo(() => {
    const set = new Set<string>()
    for (const e of logEntries) {
      const attr = attributionOf(e)
      if (attr) set.add(attr)
    }
    return [...set].sort()
  }, [logEntries])
  // Only worth a control when there's an actual choice — a Mode A page or a
  // single-page miniapp shows no file menu at all.
  const showFileFilter = fileOptions.length > 1

  // A filter pinned to something that no longer exists (cleared log, aged-out
  // session, a file that stopped logging) silently renders an empty body with
  // no clue why. Drop back to unfiltered instead.
  useEffect(() => {
    if (fileFilter !== 'all' && !fileOptions.includes(fileFilter)) setFileFilter('all')
  }, [fileOptions, fileFilter])
  useEffect(() => {
    if (sessionFilter && !sessions.some((s) => s.sess === sessionFilter)) setSessionFilter(null)
  }, [sessions, sessionFilter])

  // Filter, then group by session (newest session first, newest entry first),
  // then split each session into runs of consecutive same-file entries so a
  // consolidated miniapp log shows a clear per-file section header.
  // Text search: FREE TEXT over everything an entry displays or carries —
  // msg/stack/url/file/script/actor/ua/lvl/src/status — with every
  // whitespace-separated term matching somewhere (AND across terms,
  // case-insensitive substring per term). Anything visible in a row is
  // findable: message text, UA strings in session markers, status codes,
  // script names, the "visitor" actor stamp on public-run entries, etc.
  const grouped = useMemo(() => {
    const terms = text.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const filtered = logEntries.filter((e) => {
      if (levelFilter !== 'all' && e.lvl !== levelFilter) return false
      if (fileFilter !== 'all' && attributionOf(e) !== fileFilter) return false
      if (sessionFilter && e.sess !== sessionFilter) return false
      if (terms.length) {
        const hay = `${e.msg ?? ''} ${e.stack ?? ''} ${e.url ?? ''} ${e.file ?? ''} ${e.script ?? ''} ${e.actor ?? ''} ${e.ua ?? ''} ${e.lvl ?? ''} ${e.src ?? ''} ${e.status ?? ''}`.toLowerCase()
        if (!terms.every((term) => hay.includes(term))) return false
      }
      return true
    })
    // Section headers only make sense when the log actually mixes sources — and
    // a file filter has already reduced it to one, with the active-filter chip
    // naming it, so repeating that per session would be noise.
    const attrs = new Set(filtered.map(attributionOf))
    attrs.delete('')
    const showSections = attrs.size > 0 && fileFilter === 'all'
    const bySession = new Map<string, DebugLogEntry[]>()
    for (const e of filtered) {
      const arr = bySession.get(e.sess) ?? []
      arr.push(e)
      bySession.set(e.sess, arr)
    }
    // Order sessions by their most-recent entry, newest first.
    const sessions = [...bySession.entries()]
      .map(([sess, es]) => {
        const ordered = [...es].reverse()
        // Runs of consecutive same-attribution entries → one section each.
        const sections: Array<{ attr: string; entries: DebugLogEntry[] }> = []
        for (const e of ordered) {
          const attr = showSections ? attributionOf(e) : ''
          const last = sections[sections.length - 1]
          if (last && last.attr === attr) last.entries.push(e)
          else sections.push({ attr, entries: [e] })
        }
        return { sess, sections, showSections, latest: Math.max(...es.map((e) => e.ts)) }
      })
      .sort((a, b) => b.latest - a.latest)
    return { sessions, shown: filtered.length }
  }, [logEntries, levelFilter, fileFilter, sessionFilter, text])
  const groups = grouped.sessions
  const filterActive = levelFilter !== 'all' || fileFilter !== 'all' || !!sessionFilter || text.trim() !== ''

  const errorCount = logEntries.filter((e) => e.lvl === 'error').length

  // Portal to document.body — a `fixed` modal is trapped inside any ancestor
  // with transform/overflow (e.g. the workspace file-tree pane), which hid it
  // when opened from there. Portaling escapes that so it centers on the viewport
  // regardless of which context menu opened it.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="debug-log-viewer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {/* Frost-glass dialog (ws-glass-modal) — Liquid Glass pass. One hairline
          only, below the last chrome row (the filter row on Logs, the tab strip
          on Sessions), so scrolled content tucks beneath it; header/tabs/filters
          otherwise separate by spacing, not borders. */}
      <div className={`${themeClass} ws-glass-modal relative flex max-h-[80vh] w-full max-w-3xl flex-col`}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 pb-1 pt-3">
          <Bug size={16} className="text-purple-500" />
          <div className="min-w-0 flex-1">
            <div className="truncate themed-text font-medium">{t('debugLog.title', { name: file.name })}</div>
            <div className="themed-text-muted text-xs">
              {onRuns ? (
                runCount === null ? null : t('runLog.runsCount', { count: runCount })
              ) : tab === 'sessions' ? (
                t('debugLog.sessionsCount', { count: sessions.length })
              ) : (
                <>
                  {t('debugLog.entries', { count: logEntries.length })}
                  {errorCount > 0 && <span className="text-red-500"> · {t('debugLog.errors', { count: errorCount })}</span>}
                </>
              )}
            </div>
          </div>
          <button onClick={() => void load()} className="themed-icon rounded p-1.5 hover:themed-bg-tertiary" title={t('debugLog.refresh')} aria-label={t('debugLog.refresh')}>
            <RefreshCw size={15} />
          </button>
          {/*
            * Clear acts on the lane you are LOOKING AT, and its label says which.
            *
            * This used to be hidden on the run lane, because the run log had no
            * mutation route at all. It has one now, and the hazard moved rather
            * than disappearing: one button over a lane selector is a button whose
            * meaning depends on a chip somewhere else, so a user filtered to
            * `error` who clears would have wiped their run history. The menu's
            * heading and items name the record being destroyed, every time.
            */}
          {tab !== 'sessions' && (
          <div ref={clearMenuRef} className="relative">
            <button onClick={() => setShowClearMenu((v) => !v)} className="themed-icon rounded p-1.5 hover:themed-bg-tertiary" title={onRuns ? t('runLog.clear') : t('debugLog.clear')} aria-label={onRuns ? t('runLog.clear') : t('debugLog.clear')} data-testid="debug-log-clear">
              <Trash2 size={15} />
            </button>
            {/* Scoped-clear popup — frost-glass kit. Rendered INSIDE the glass
                modal, so its own backdrop blur can't reach the page (nested
                backdrop root); the `.ws-glass-modal .ws-menu` CSS gives it a
                near-opaque base instead. */}
            {showClearMenu && (
              <div className="ws-menu absolute right-0 top-full z-10 mt-1 w-max min-w-[160px]" data-testid="debug-log-clear-menu">
                {/* Names the record, not the verb — this menu can destroy either
                    of two different things depending on the active lane. */}
                <div className="px-2.5 py-1 text-[10px] uppercase tracking-wide themed-text-muted">{onRuns ? t('runLog.clear') : t('debugLog.clear')}</div>
                <button type="button" onClick={() => void (onRuns ? clearRuns(3_600_000) : clear(3_600_000))} className="ws-menu-item cursor-pointer">{t('debugLog.clearLastHour')}</button>
                <button type="button" onClick={() => void (onRuns ? clearRuns(3 * 3_600_000) : clear(3 * 3_600_000))} className="ws-menu-item cursor-pointer">{t('debugLog.clearLast3h')}</button>
                <button type="button" onClick={() => void (onRuns ? clearRuns(24 * 3_600_000) : clear(24 * 3_600_000))} className="ws-menu-item cursor-pointer">{t('debugLog.clearLastDay')}</button>
                <div className="ws-menu-sep" />
                {/* Destructive whole-record wipe — visually set apart (danger red). */}
                <button type="button" onClick={() => void (onRuns ? clearRuns() : clear())} className="ws-menu-item ws-menu-item-danger cursor-pointer font-medium" data-testid="debug-log-clear-all">
                  <Trash2 size={13} className="shrink-0" />
                  {onRuns ? t('runLog.clearAll') : t('debugLog.clearAll')}
                </button>
              </div>
            )}
          </div>
          )}
          <button onClick={onClose} className="themed-icon rounded p-1.5 hover:themed-bg-tertiary" title={t('debugLog.close')} aria-label={t('debugLog.close')}>
            <X size={16} />
          </button>
        </div>

        {/* Tabs — underline strip, purple accent to match the header's Bug icon.
            It carries the chrome hairline on the Sessions tab, where the filter
            row (which owns it on the Logs tab) isn't rendered — the modal keeps
            exactly one hairline either way. */}
        <div role="tablist" className={`flex items-center gap-4 px-4 ${tab !== 'logs' ? 'border-b themed-border-secondary' : ''}`}>
          {(['logs', 'sessions'] as const).map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              role="tab"
              aria-selected={tab === tb}
              aria-controls="debug-log-panel"
              data-testid={`debug-log-tab-${tb}`}
              className={`-mb-px border-b-2 px-0.5 py-1.5 text-xs transition-colors ${
                tab === tb ? 'border-purple-500 themed-text font-medium' : 'border-transparent themed-text-muted hover:themed-text'
              }`}
            >
              {t(tb === 'logs' ? 'debugLog.tabLogs' : 'debugLog.tabSessions')}
            </button>
          ))}
        </div>

        {/* Filters — chip row on regular widths; below `sm` the level chips
            consolidate into a glass dropdown so the row fits narrow screens.
            Logs-only: the Sessions tab is a short, already-scannable list. */}
        {tab === 'logs' && (
        <div className="flex items-center gap-2 border-b themed-border-secondary px-4 py-2.5">
          <div className="hidden items-center gap-1 sm:flex">
            {(['all', 'error', 'warn', 'network'] as const).map((lv) => (
              <button
                key={lv}
                onClick={() => setLevelFilter(lv)}
                className={`rounded px-2 py-0.5 text-xs ${levelFilter === lv ? 'themed-btn-primary' : 'themed-btn-secondary'}`}
              >
                {/* Console level names (error/warn/network) are dev-tool jargon —
                    kept verbatim; only the "all" label is translated. */}
                {lv === 'all' ? t('debugLog.filterAll') : lv}
              </button>
            ))}
            {/*
              * A SEPARATOR, not a gap. Runs sits in this row because it answers
              * the same question the other chips do — "what went wrong?" — but it
              * is not a level, and it swaps the body rather than narrowing it.
              * The rule is `aria-hidden` decoration: the accessible distinction
              * is carried by the chips' own labels, and a screen reader gains
              * nothing from a vertical line.
              */}
            <span aria-hidden className="mx-1 h-4 w-px shrink-0 themed-border-secondary" style={{ backgroundColor: 'currentColor', opacity: 0.2 }} />
            <button
              onClick={() => setLevelFilter('runs')}
              data-testid="debug-log-lane-runs"
              className={`rounded px-2 py-0.5 text-xs ${levelFilter === 'runs' ? 'themed-btn-primary' : 'themed-btn-secondary'}`}
            >
              {t('runLog.tabRuns')}
            </button>
          </div>
          <div ref={levelMenuRef} className="relative sm:hidden">
            <button
              onClick={() => setShowLevelMenu((v) => !v)}
              className="themed-btn-secondary flex items-center gap-1 rounded px-2 py-0.5 text-xs"
              data-testid="debug-log-level-menu-btn"
            >
              {levelFilter === 'all' ? t('debugLog.filterAll') : levelFilter === 'runs' ? t('runLog.tabRuns') : levelFilter}
              <ChevronDown size={12} className="shrink-0" />
            </button>
            {showLevelMenu && (
              <div className="ws-menu absolute left-0 top-full z-10 mt-1 w-max min-w-[130px]" data-testid="debug-log-level-menu">
                {(['all', 'error', 'warn', 'network'] as const).map((lv) => (
                  <button
                    key={lv}
                    type="button"
                    onClick={() => { setLevelFilter(lv); setShowLevelMenu(false) }}
                    className={`ws-menu-item cursor-pointer ${levelFilter === lv ? '' : 'themed-text-secondary'}`}
                  >
                    <span className="inline-block w-3 text-center text-[10px] themed-text-muted">{levelFilter === lv ? '✓' : ''}</span>
                    <span className="flex-1">{lv === 'all' ? t('debugLog.filterAll') : lv}</span>
                  </button>
                ))}
                {/* The desktop row's separator, in the shape this menu uses. */}
                <div className="ws-menu-sep" />
                <button
                  type="button"
                  onClick={() => { setLevelFilter('runs'); setShowLevelMenu(false) }}
                  className={`ws-menu-item cursor-pointer ${levelFilter === 'runs' ? '' : 'themed-text-secondary'}`}
                >
                  <span className="inline-block w-3 text-center text-[10px] themed-text-muted">{levelFilter === 'runs' ? '✓' : ''}</span>
                  <span className="flex-1">{t('runLog.tabRuns')}</span>
                </button>
              </div>
            )}
          </div>
          {/* File filter — the axis that actually narrows a consolidated miniapp
              log. Hidden entirely when the log only spans one file. */}
          {showFileFilter && (
            <div ref={fileMenuRef} className="relative min-w-0">
              <button
                onClick={() => setShowFileMenu((v) => !v)}
                className={`flex max-w-[9rem] items-center gap-1 rounded px-2 py-0.5 text-xs sm:max-w-[12rem] ${fileFilter === 'all' ? 'themed-btn-secondary' : 'themed-btn-primary'}`}
                title={fileFilter === 'all' ? t('debugLog.fileFilter') : fileFilter}
                data-testid="debug-log-file-menu-btn"
              >
                <FileCode size={12} className="shrink-0" />
                <span className="truncate">{fileFilter === 'all' ? t('debugLog.fileAll') : fileFilter}</span>
                <ChevronDown size={12} className="shrink-0" />
              </button>
              {showFileMenu && (
                <div className="ws-menu absolute left-0 top-full z-10 mt-1 max-h-64 w-max min-w-[160px] max-w-[18rem] overflow-auto" data-testid="debug-log-file-menu">
                  {(['all', ...fileOptions] as const).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => { setFileFilter(opt); setShowFileMenu(false) }}
                      className={`ws-menu-item cursor-pointer ${fileFilter === opt ? '' : 'themed-text-secondary'}`}
                      title={opt === 'all' ? undefined : opt}
                    >
                      <span className="inline-block w-3 shrink-0 text-center text-[10px] themed-text-muted">{fileFilter === opt ? '✓' : ''}</span>
                      <span className="flex-1 truncate">{opt === 'all' ? t('debugLog.fileAll') : opt}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Active session scope — set by a Sessions-tab card, dismissable here
              (nothing else in this row can clear it). */}
          {sessionFilter && (
            <button
              onClick={() => setSessionFilter(null)}
              className="themed-btn-primary flex min-w-0 shrink items-center gap-1 rounded px-2 py-0.5 text-xs"
              title={t('debugLog.clearSessionFilter')}
              data-testid="debug-log-session-chip"
            >
              <span className="truncate">{t('debugLog.sessionLabel')} {sessionFilter}</span>
              <X size={11} className="shrink-0" />
            </button>
          )}
          {/* Shown/total while any filter is active — numeric, locale-neutral. */}
          {filterActive && (
            <span className="themed-text-muted ml-auto shrink-0 text-[10px] tabular-nums" data-testid="debug-log-filter-count">
              {grouped.shown}/{logEntries.length}
            </span>
          )}
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('debugLog.filterPlaceholder')}
            className={`themed-input w-24 min-w-0 shrink-0 rounded px-2 py-1 text-xs sm:w-40 ${filterActive ? '' : 'ml-auto'}`}
          />
        </div>
        )}

        {/* Body — min-height so the modal never shrinks below the scoped-clear
            popup's reach: the popup is an absolute child of the header and
            `ws-glass-modal` clips (overflow hidden), so a near-empty log would
            otherwise cut off "Clear all" with no way to scroll to it. */}
        {/* Monospace is for log rows — the session cards are prose + numbers. */}
        <div id="debug-log-panel" role="tabpanel" className={`min-h-[260px] flex-1 overflow-auto px-4 py-3 text-xs ${tab === 'logs' ? 'font-mono' : ''}`}>
          {/* Runs is rendered ALWAYS and merely hidden, not mounted on demand.
              It owns a SEPARATE request from the debug log's, so remounting it
              on every tab switch would refetch each time; staying mounted is
              what makes switching back instant. It is also OUTSIDE the chain
              below on purpose — that chain's `loading`/`error` belong to the
              debug-log fetch, and gating a second surface on a first surface's
              failure is how one broken request becomes two broken panels. */}
          <div hidden={!onRuns}>
            <AppRunHistory fileId={file.id} active={onRuns} onCount={setRunCount} refreshToken={runsRefresh} />
          </div>
          {onRuns ? null : loading ? (
            <div className="themed-text-muted py-8 text-center">{t('debugLog.loading')}</div>
          ) : error === 'forbidden' ? (
            <div className="themed-text-muted flex flex-col items-center gap-2 py-8 text-center">
              <AlertCircle size={20} className="text-amber-500" />
              {t('debugLog.forbidden')}
            </div>
          ) : error === 'failed' ? (
            <div className="themed-text-muted py-8 text-center">{t('debugLog.loadFailed')}</div>
          ) : tab === 'sessions' ? (
            sessions.length === 0 ? (
              <div className="themed-text-muted py-8 text-center">{t('debugLog.emptyNoSessions')}</div>
            ) : (
              sessions.map((s) => {
                const uaLabel = describeUa(s.ua)
                const files = [...s.files]
                return (
                  // The whole card is the control: it scopes the Logs tab to this
                  // run — the only thing a reader wants next from a session row.
                  <button
                    key={s.sess}
                    type="button"
                    onClick={() => openSession(s.sess)}
                    title={t('debugLog.viewSessionLogs')}
                    data-testid="debug-log-session-card"
                    className="mb-2 block w-full rounded-lg ws-glass-well px-3 py-2 text-left last:mb-1 hover:themed-bg-tertiary"
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="min-w-0 truncate themed-text font-medium">{fmtSessionDate(s.start)}</span>
                      <span className="themed-text-muted shrink-0 tabular-nums text-[10px]">{fmtDuration(s.end - s.start)}</span>
                      <span className="ml-auto shrink-0 tabular-nums text-[10px] themed-text-muted">
                        {t('debugLog.entries', { count: s.total })}
                        {s.errors > 0 && <span className="text-red-500"> · {t('debugLog.errors', { count: s.errors })}</span>}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] themed-text-secondary">
                      {/* Raw UA on the title attr — the parse is best-effort, and
                          free-text search still reaches the stored original. */}
                      {(uaLabel || s.ua) && (
                        <span className="flex min-w-0 items-center gap-1" title={s.ua}>
                          <Monitor size={10} className="shrink-0" />
                          <span className="truncate">{uaLabel ?? s.ua}</span>
                        </span>
                      )}
                      {s.actor && (
                        <span className="flex min-w-0 items-center gap-1">
                          <User size={10} className="shrink-0" />
                          <span className="truncate">{s.actor}</span>
                        </span>
                      )}
                      {files.length > 0 && (
                        <span className="flex min-w-0 items-center gap-1" title={files.join(', ')}>
                          {files.length === 1 && files[0].startsWith('backend') ? <Server size={10} className="shrink-0" /> : <FileCode size={10} className="shrink-0" />}
                          <span className="truncate">
                            {files.slice(0, SESSION_FILES_SHOWN).join(', ')}
                            {files.length > SESSION_FILES_SHOWN ? ` +${files.length - SESSION_FILES_SHOWN}` : ''}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="mt-1 font-mono text-[10px] themed-text-muted">
                      {t('debugLog.sessionLabel')} {s.sess}
                    </div>
                  </button>
                )
              })
            )
          ) : groups.length === 0 ? (
            <div className="themed-text-muted py-8 text-center">
              {logEntries.length === 0 ? t('debugLog.emptyNoLogs') : t('debugLog.emptyNoMatch')}
            </div>
          ) : (
            groups.map((g) => (
              // Each session is a soft glass WELL — a grouped block, not a
              // divider — so consecutive sessions in a single-file log read as
              // distinct runs even though their rows look alike. Per-file
              // section chips inside sit one tint-level above the well.
              <div key={g.sess} className="mb-3 rounded-lg ws-glass-well px-2.5 py-2 last:mb-1">
                {/* Session marker — micro-label with the full day/date/timezone;
                    entry rows below stay time-only, so which day a row belongs
                    to is never ambiguous. */}
                <div className="themed-text-muted mb-1.5 text-[10px]">
                  <span className="uppercase tracking-wide">{t('debugLog.sessionLabel')} {g.sess}</span>
                  <span> · {fmtSessionDate(g.latest)}</span>
                </div>
                {g.sections.map((s, si) => (
                  <div key={si}>
                    {/* Per-file section header — the visual separation between
                        files inside a miniapp's consolidated log. */}
                    {g.showSections && (
                      <div
                        className="mt-2 mb-0.5 flex items-center gap-1.5 rounded ws-glass-chip px-1.5 py-0.5 text-[10px] font-medium themed-text-secondary"
                        data-testid="debug-log-file-section"
                      >
                        {/* Server icon for backend-produced sections (script tees),
                            file icon for browser pages — judged by the entries' src,
                            not the label (backend sections can be labeled by file). */}
                        {s.entries[0]?.src === 'backend' ? <Server size={10} className="shrink-0" /> : <FileCode size={10} className="shrink-0" />}
                        <span className="truncate">{s.attr || t('debugLog.appSection')}</span>
                      </div>
                    )}
                    {s.entries.map((e, i) => {
                      const long = isLongEntry(e)
                      const open = expanded.has(e)
                      return (
                        // Errors get a soft tint block so they scan at a glance
                        // in a long log (the level color alone is easy to miss).
                        // Long entries render collapsed (2-line clamp, stack
                        // hidden) and toggle open on click.
                        <div
                          key={i}
                          className={`flex gap-2 rounded px-1.5 py-[3px] leading-relaxed ${g.showSections ? 'pl-2' : ''} ${e.lvl === 'error' ? 'bg-red-500/10' : ''} ${long ? 'cursor-pointer' : ''}`}
                          onClick={long ? () => toggleExpanded(e) : undefined}
                          title={long ? t(open ? 'debugLog.collapseHint' : 'debugLog.expandHint') : undefined}
                        >
                          <span className="themed-text-muted shrink-0 tabular-nums">{fmtTime(e.ts)}</span>
                          {/* src column yields on narrow screens — the per-file
                              section headers / level colors carry the signal. */}
                          <span className="themed-text-muted hidden w-14 shrink-0 sm:inline">{SRC_LABEL[e.src] ?? e.src}</span>
                          <span className={`shrink-0 w-14 ${LEVEL_STYLE[e.lvl ?? ''] ?? 'themed-text-secondary'}`}>
                            {e.lvl ?? ''}
                          </span>
                          <div className="min-w-0 flex-1 themed-text">
                            <div className={`whitespace-pre-wrap break-words ${long && !open ? 'line-clamp-2' : ''}`}>
                              {e.msg ?? ''}
                              {e.n && e.n > 1 ? <span className="themed-text-muted"> ×{e.n}</span> : null}
                              {e.status ? <span className="themed-text-muted"> [{e.status}]</span> : null}
                              {e.script && !g.showSections ? <span className="themed-text-muted"> ({e.script})</span> : null}
                            </div>
                            {/* "stack" is dev-tool jargon — kept verbatim, like the level names. */}
                            {e.stack && !open ? <span className="themed-text-muted text-[10px]">▸ stack</span> : null}
                            {e.stack && open ? <div className="themed-text-muted mt-0.5 whitespace-pre-wrap break-words opacity-80">{e.stack}</div> : null}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
